// src/pages/calls/index.tsx

import { motion } from 'framer-motion';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { CallsTable } from './components/calls-table';
import { CallsFilters, type CallFilters } from './components/calls-filters';
import { getAllCalls } from '@/api/calls/calls.api';
import type { Call } from '@/api/calls/calls.types';
import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { Pagination } from "@/components/ui/pagination";
import { useSearchParams } from 'react-router-dom';
import { SiteHeader } from '@/components/site-header';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { CalendarDatePicker } from '@/components/calendar-date-picker';
import { cn } from '@/lib/utils';

const Calls = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [calls, setCalls] = useState<Call[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(() => {
        const page = searchParams.get('page');
        return page ? parseInt(page) : 1;
    });
    const [pageSize, setPageSize] = useState(() => {
        const size = searchParams.get('size');
        return size ? parseInt(size) : 10;
    });
    
    // Initialize filters from URL params
    const [filters, setFilters] = useState<CallFilters>(() => {
        const fromDate = searchParams.get('from');
        const toDate = searchParams.get('to');
        
        // Parse comma-separated values from URL
        const parseArrayParam = (param: string | null): string[] => {
            return param ? param.split(',').filter(Boolean) : [];
        };
        
        return {
            ids: parseArrayParam(searchParams.get('ids')),
            assistant_ids: parseArrayParam(searchParams.get('assistant_ids')),
            phone_number_ids: parseArrayParam(searchParams.get('phone_number_ids')),
            phone_numbers: parseArrayParam(searchParams.get('phone_numbers')),
            customer_phone_numbers: parseArrayParam(searchParams.get('customer_phone_numbers')),
            ended_reasons: parseArrayParam(searchParams.get('ended_reasons')),
            type: (searchParams.get('type') as 'inboundPhoneCall' | 'outboundPhoneCall' | 'webCall') || '',
            dateRange: {
                from: fromDate ? new Date(fromDate) : undefined,
                to: toDate ? new Date(toDate) : undefined
            }
        };
    });
    
    const { error } = useEnhancedToast();
    
    // Use refs to prevent infinite loops
    const isInitialMount = useRef(true);
    const filtersRef = useRef(filters);

    const handleError = useCallback((err: unknown) => {
        error({
            icon: AlertCircle,
            title: "Failed to Load Calls",
            description: err instanceof Error ? err.message : "Could not fetch call history. Please try again.",
            duration: 4000,
        });
    }, []);

    const fetchCalls = useCallback(async () => {
        setIsLoading(true);
        try {
            const skip = (currentPage - 1) * pageSize;
            
            // Pagination parameters
            const paginationParams = { skip, limit: pageSize };
            
            // Filter parameters for request body
            const filterParams: any = {
                // Send arrays directly
                ...(filters.ids && filters.ids.length > 0 && { id: filters.ids }),
                ...(filters.assistant_ids && filters.assistant_ids.length > 0 && { assistant_id: filters.assistant_ids }),
                ...(filters.phone_number_ids && filters.phone_number_ids.length > 0 && { phone_number_id: filters.phone_number_ids }),
                ...(filters.phone_numbers && filters.phone_numbers.length > 0 && { phone_number: filters.phone_numbers }),
                ...(filters.customer_phone_numbers && filters.customer_phone_numbers.length > 0 && { customer_phone_number: filters.customer_phone_numbers }),
                ...(filters.ended_reasons && filters.ended_reasons.length > 0 && { ended_reason: filters.ended_reasons }),
                ...(filters.type && { type: [filters.type] }), // Convert single type to array
            };
            
            // Add date parameters if date range is selected
            if (filters.dateRange.from) {
                const startDate = new Date(filters.dateRange.from);
                startDate.setHours(0, 0, 0, 0);
                filterParams.start_date = startDate.toISOString();
            }
            
            if (filters.dateRange.to) {
                const endDate = new Date(filters.dateRange.to);
                endDate.setHours(23, 59, 59, 999);
                filterParams.end_date = endDate.toISOString();
            }
            
            console.log('API Request - Pagination:', paginationParams);
            console.log('API Request - Filters:', filterParams);
            
            const { data, total } = await getAllCalls(paginationParams, filterParams);
            
            setCalls(data);
            setTotalItems(total);
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, handleError, filters]);

    // Initial load and pagination changes
    useEffect(() => {
        fetchCalls();
    }, [currentPage, pageSize]);

    // Handle filter changes
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Reset to first page when filters change
        setCurrentPage(1);
        fetchCalls();
    }, [filters]);

    // Update URL params when filters or pagination changes
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        params.set('page', currentPage.toString());
        params.set('size', pageSize.toString());
        
        // Add filter params to URL
        if (filters.ids && filters.ids.length > 0) params.set('ids', filters.ids.join(','));
        else params.delete('ids');
        
        if (filters.assistant_ids && filters.assistant_ids.length > 0) params.set('assistant_ids', filters.assistant_ids.join(','));
        else params.delete('assistant_ids');
        
        if (filters.phone_number_ids && filters.phone_number_ids.length > 0) params.set('phone_number_ids', filters.phone_number_ids.join(','));
        else params.delete('phone_number_ids');
        
        if (filters.phone_numbers && filters.phone_numbers.length > 0) params.set('phone_numbers', filters.phone_numbers.join(','));
        else params.delete('phone_numbers');
        
        if (filters.customer_phone_numbers && filters.customer_phone_numbers.length > 0) params.set('customer_phone_numbers', filters.customer_phone_numbers.join(','));
        else params.delete('customer_phone_numbers');
        
        if (filters.ended_reasons && filters.ended_reasons.length > 0) params.set('ended_reasons', filters.ended_reasons.join(','));
        else params.delete('ended_reasons');
        
        if (filters.type) params.set('type', filters.type);
        else params.delete('type');
        
        if (filters.dateRange.from) params.set('from', filters.dateRange.from.toISOString());
        else params.delete('from');
        
        if (filters.dateRange.to) params.set('to', filters.dateRange.to.toISOString());
        else params.delete('to');
        
        setSearchParams(params);
    }, [currentPage, pageSize, filters, setSearchParams]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleFiltersChange = (newFilters: CallFilters) => {
        setFilters(newFilters);
    };

    const handleRefresh = async () => {
        await fetchCalls();
    };

    const callTypeOptions = [
        { value: '', label: 'All Call Types' },
        { value: 'inboundPhoneCall', label: 'Inbound' },
        { value: 'outboundPhoneCall', label: 'Outbound' },
        { value: 'webCall', label: 'Web Call' },
    ];

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Call Logs"
                subtitle="Track and manage your call history"
            >
                {/* Call Type Filter - Combobox (outline by default) */}
                <Combobox
                    options={callTypeOptions}
                    value={filters.type || ''}
                    onValueChange={(value) => {
                        setFilters(prev => ({ ...prev, type: value as any }));
                    }}
                    placeholder="Filter by type..."
                    className="w-[160px]"
                />

                {/* Advanced Filters & Clear All - outline (consistent with filters) */}
                <CallsFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onRefresh={handleRefresh}
                    isLoading={isLoading}
                    compact={true}
                />

                {/* Date Range Picker - Button (outline by default) */}
                <CalendarDatePicker
                    date={filters.dateRange}
                    onDateSelect={(range) => {
                        setFilters(prev => ({ ...prev, dateRange: range }));
                    }}
                    numberOfMonths={2}
                />

                {/* Refresh Button - Primary action */}
                <Button
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isLoading}
                    className="gap-2"
                >
                    <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                    Refresh
                </Button>
            </SiteHeader>

            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8 space-y-6"
                >
                    <CallsTable
                        calls={calls}
                        onRefresh={handleRefresh}
                        isLoading={isLoading}
                        currentPage={currentPage}
                        pageSize={pageSize}
                    />

                    {totalItems > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalItems={totalItems}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default Calls;
